import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { orgsApi, type OrgRow } from '../features/orgs/api.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDate } from '../lib/dvDate.ts';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 20;

export function AdminOrgs(): JSX.Element {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'orgs', page],
    queryFn: () => orgsApi.list(page, PAGE_SIZE),
  });
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const create = useMutation({
    mutationFn: () => orgsApi.create(name, contactEmail),
    onSuccess: async () => {
      setName('');
      setContactEmail('');
      await qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="font-serif text-3xl tracking-tight">{dv.orgs.title}</h1>
      </header>

      <section className="card-paper space-y-3 p-4">
        <h2 className="status-label text-muted-foreground">{dv.orgs.create}</h2>
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && contactEmail.trim()) create.mutate();
          }}
        >
          <input
            className="input flex-1"
            placeholder={dv.orgs.name}
            aria-label={dv.orgs.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            className="input flex-1"
            type="email"
            dir="ltr"
            placeholder={dv.orgs.contactEmail}
            aria-label={dv.orgs.contactEmail}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={create.isPending}
            className="btn-primary"
          >
            {dv.orgs.create}
          </button>
        </form>
      </section>

      <section className="card-paper">
        <table className="w-full text-sm">
          <caption className="sr-only">{dv.orgs.title}</caption>
          <thead>
            <tr>
              <th className="status-label text-muted-foreground p-3 text-start">{dv.orgs.name}</th>
              <th className="status-label text-muted-foreground p-3 text-start hidden sm:table-cell">{dv.orgs.contactEmail}</th>
              <th className="status-label text-muted-foreground p-3 text-start">{dv.orgs.users}</th>
              <th className="status-label text-muted-foreground p-3 text-start hidden sm:table-cell">{dv.orgs.created}</th>
              <th className="status-label text-muted-foreground p-3 text-start">{dv.common.active}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-muted-foreground" role="status" aria-live="polite">
                  {dv.common.loading}
                </td>
              </tr>
            )}
            {data?.orgs.map((o: OrgRow) => (
              <tr key={o.id} className="border-t border-border">
                <td className="p-3">
                  <Link
                    to="/admin/orgs/$orgId"
                    params={{ orgId: o.id }}
                    className="text-primary hover:underline"
                  >
                    {o.name}
                  </Link>
                </td>
                <td className="p-3 hidden sm:table-cell text-muted-foreground" dir="ltr">{o.contactEmail}</td>
                <td className="p-3 text-muted-foreground">{o._count.users}</td>
                <td className="p-3 text-muted-foreground hidden sm:table-cell">{formatDvDate(o.createdAt)}</td>
                <td className="p-3">
                  <span
                    className={`status-label rounded-full px-2 py-0.5 text-xs ${
                      o.status === 'ACTIVE'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {o.status === 'ACTIVE' ? dv.common.active : dv.common.suspended}
                  </span>
                </td>
              </tr>
            ))}
            {data && data.orgs.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-muted-foreground">{dv.orgs.empty}</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <Pagination
        page={data?.page ?? 1}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        total={data?.total ?? 0}
        onChange={setPage}
      />
    </div>
  );
}
